import { useContext, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'

import { FALLBACK_DOCUMENT_TYPE_ID } from '../../../models/DocumentType'
import { DocumentRecord } from '../../../models/Document'

import {
  selectDocument,
  selectDocumentContact,
  selectDocumentContactIsLoading,
  selectDocumentFileInfoIsLoading,
  selectDocumentLoading,
} from '../../../store/processDocument/document'
import { selectDocumentSummaryResult } from '../../../store/processDocument/documentSummary'
import { selectDocumentBatchLoading } from '../../../store/processDocument/documentBatch'
import { selectDocumentType } from '../../../store/processDocument/processDocument'
import { selectIsHistoryMode } from '../../../store/meetingSummary/contactUpdate'
import { useAppSelector } from '../../../store/hooks'

import { ExternalUpdateHistoryView } from '../../UpdateExternalContact/ExternalUpdateHistoryView'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'

import { ProcessDocumentFileUploadProgress, ProgressItem, ProgressItemWrapper } from '../ProcessDocumentFileUploadProgress/ProcessDocumentFileUploadProgress'
import { DocumentSummary } from '../DocumentSummary/DocumentSummary'
import { DocumentSidebar } from '../DocumentSidebar/DocumentSidebar'
import { getDocumentFile } from '../utils'

import { Spinner } from '../../../components/Spinner/Spinner'

import { DocumentHeader } from './DocumentHeader'

import './Document.scss'

export const Document = () => {
  const document = useAppSelector(selectDocument)
  const contact = useAppSelector(selectDocumentContact)
  const isHistoryMode = useAppSelector(selectIsHistoryMode)

  const documentLoading = useAppSelector(selectDocumentLoading)
  const contactLoading = useAppSelector(selectDocumentContactIsLoading)
  const documentBatchLoading = useAppSelector(selectDocumentBatchLoading)
  const fileInfoLoading = useAppSelector(selectDocumentFileInfoIsLoading)

  const { portalRef } = useContext(HeaderContext)

  const [headerContainer, setHeaderContainer] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (portalRef && portalRef.current) {
      setHeaderContainer(portalRef.current)
    }
  }, [portalRef])

  if (documentLoading) {
    return <DocumentLoader />
  }

  if (!document) {
    return null
  }

  if (contactLoading) {
    return <DocumentLoader />
  }

  if (!contact) {
    return null
  }

  if (documentBatchLoading || fileInfoLoading) {
    return <DocumentLoader />
  }

  return (
    <>
      {headerContainer ? createPortal(<DocumentHeader document={document} />, headerContainer) : null}
      {isHistoryMode ? (
        <ExternalUpdateHistoryView />
      ) : document.summaryProcessing.finishedAt ? (
        <DocumentSummary document={document} contact={contact} />
      ) : (
        <DocumentProcessingProgress document={document} />
      )}
      <DocumentSidebar />
    </>
  )
}

const DocumentLoader = () => {
  return (
    <div className='document-loader d-flex flex-column justify-content-center align-items-center flex-shrink-0 align-self-stretch p-4 zocks-gap-1.5 zocks-border-0.75'>
      <Spinner />
      <span className='paragraph-small medium zocks-text-gray-900'>Loading document…</span>
    </div>
  )
}

interface DocumentProcessingProgressProps {
  document: DocumentRecord
}

const DocumentProcessingProgress: React.FC<DocumentProcessingProgressProps> = ({ document }) => {
  const { typeProcessing, summaryProcessing } = document

  const documentSummaryResult = useAppSelector(selectDocumentSummaryResult)
  const documentType = useAppSelector(selectDocumentType)

  const fileName = getDocumentFile(document)?.name

  const progressItems: ProgressItem[] = useMemo(() => {
    const typeFinished = !!typeProcessing.finishedAt
    const typeSuccess = typeFinished && !!documentType
    const summarySuccess = !!summaryProcessing.finishedAt && !!documentSummaryResult

    const typeLoading = !typeFinished
    const summaryLoading = typeSuccess && !summarySuccess

    return [
      {
        isLoading: false,
        isSuccess: true,
        label: <ProgressItemWrapper isLoading={false}>Uploaded {fileName}</ProgressItemWrapper>,
      },
      {
        isLoading: typeLoading,
        isSuccess: typeSuccess,
        label: (
          <ProgressItemWrapper isLoading={typeLoading}>
            {documentType ? (
              documentType.systemDocumentTypeId === FALLBACK_DOCUMENT_TYPE_ID ? (
                <>
                  Couldn't match document type → Using <strong>{documentType.name}</strong> as fallback
                </>
              ) : (
                <>
                  Identified as <strong>{typeProcessing.label}</strong> → Applying <strong>{documentType.name}</strong> template
                </>
              )
            ) : (
              'Analyzing document type…'
            )}
          </ProgressItemWrapper>
        ),
      },
      {
        isLoading: summaryLoading,
        isSuccess: summarySuccess,
        label: (
          <ProgressItemWrapper isLoading={summaryLoading}>
            {documentSummaryResult ? 'Summary ready to review and sync' : 'Extracting insights and creating summary…'}
          </ProgressItemWrapper>
        ),
      },
    ]
  }, [documentSummaryResult, documentType, fileName, summaryProcessing, typeProcessing])

  return (
    <div className='d-flex justify-content-center zocks-pt-4.5'>
      <div className='d-flex flex-column zocks-gap-3 document-processing-progress-container'>
        <div className='d-flex flex-column gap-3 align-items-center document-processing-progress-inner-container text-center'>
          <h2>Processing document</h2>
          <span>Your AI Assistant is extracting insights and preparing updates for your CRM and financial systems.</span>
        </div>
        <ProcessDocumentFileUploadProgress items={progressItems} />
      </div>
    </div>
  )
}
